import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

const COLORS = [
  '#D4AF37', // Atlas gold
  '#8B0000', // maroon
  '#14213D', // navy
  '#2E7D32', // forest green
  '#7A93AC', // slate
  '#B85C38', // rust
  '#6B46C1', // purple
  '#0E7C86', // teal
];

export default function FolderDialog({ open, folder, onClose, onCreate, onUpdate }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(folder?.name || '');
      setColor(folder?.color || COLORS[0]);
    }
  }, [open, folder]);

  const isEdit = !!folder;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      if (isEdit) await onUpdate(folder.id, { name: trimmed, color });
      else await onCreate({ name: trimmed, color });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="folder-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl text-[#14213D]">
            {isEdit ? 'Edit folder' : 'New folder'}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#5A6577]">
            Folders help you group lessons by unit, grade, or theme.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="folder-name" className="text-xs uppercase tracking-wider text-[#5A6577]">Folder name</Label>
            <Input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Grade 9 US History – Unit 4"
              maxLength={80}
              autoFocus
              required
              data-testid="folder-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[#5A6577]">Color</Label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === c ? 'border-[#14213D] ring-2 ring-[#14213D]/20 scale-110' : 'border-white shadow-sm'
                  }`}
                  style={{ background: c }}
                  aria-label={`Color ${c}`}
                  data-testid={`folder-color-${c}`}
                />
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose} data-testid="folder-cancel-btn">Cancel</Button>
            <Button
              type="submit"
              disabled={saving || !name.trim()}
              className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]"
              data-testid="folder-save-btn"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create folder'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
